import { useQuery } from '@tanstack/react-query';
import { quotesApi } from '@/lib/api/client';

export const quoteKeys = {
  all: ['quotes'] as const,
  list: (symbols: string[]) => [...quoteKeys.all, 'list', symbols.sort().join(',')] as const,
  option: (symbol: string) => [...quoteKeys.all, 'option', symbol] as const,
  optionLegs: (underlying: string, legs: OptionLegQuery[]) =>
    [...quoteKeys.all, 'optionLegs', underlying, JSON.stringify(legs)] as const,
  legsBatch: (groups: Array<{ id: string; underlyingSymbol: string; legs: OptionLegQuery[] }>) =>
    [...quoteKeys.all, 'legsBatch', JSON.stringify(groups)] as const,
};

export interface OptionLegQuery {
  optionType: 'call' | 'put';
  strikePrice: number;
  expirationDate: string;
}

export function useQuotes(symbols: string[]) {
  return useQuery({
    queryKey: quoteKeys.list(symbols),
    queryFn: () => quotesApi.get(symbols),
    // Transform array to object keyed by symbol for easy lookup
    select: (data) => {
      const map: Record<string, { price: number; change: number; changePercent: number }> = {};
      for (const quote of data.quotes) {
        map[quote.symbol] = quote;
      }
      return map;
    },
    enabled: symbols.length > 0,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // 1 minute
  });
}

export function useOptionQuote(symbol: string) {
  return useQuery({
    queryKey: quoteKeys.option(symbol),
    queryFn: () => quotesApi.getOption(symbol),
    select: (data) => data.quote,
    enabled: !!symbol,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

interface RollLegQuery {
  optionType: 'call' | 'put';
  strikePrice: number;
  expirationDate: string;
  action: 'BTO' | 'STO' | 'BTC' | 'STC';
  quantity: number;
}

interface TargetStrike {
  optionType: 'call' | 'put';
  originalStrikePrice: number;
  strikePrice: number;
}

export function useRollScenarios(
  underlyingSymbol: string,
  legs: RollLegQuery[],
  enabled = true,
  targetStrikes?: TargetStrike[]
) {
  return useQuery({
    queryKey: [...quoteKeys.all, 'rollScenarios', underlyingSymbol, JSON.stringify(legs), JSON.stringify(targetStrikes)],
    queryFn: () => quotesApi.getRollScenarios(underlyingSymbol, legs, targetStrikes),
    enabled: enabled && !!underlyingSymbol && legs.length > 0,
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 2 * 60 * 1000, // 2 minutes
  });
}

export function useOptionLegsQuotes(
  underlyingSymbol: string,
  legs: OptionLegQuery[],
  enabled = true
) {
  return useQuery({
    queryKey: quoteKeys.optionLegs(underlyingSymbol, legs),
    queryFn: () => quotesApi.getOptionLegs(underlyingSymbol, legs),
    enabled: enabled && !!underlyingSymbol && legs.length > 0,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/**
 * The shape the Risk board's rows present to this hook. Declared structurally rather than by
 * importing `RiskRow`, so a query hook never depends on a component module.
 *
 * `rowKey`, not `chainId`: a chain whose sides sit at two expirations now produces two rows, and
 * grouping by `chainId` would fold both rows' short legs into one request group, handing one of
 * them the other's odds.
 */
export interface RiskBoardRow {
  rowKey: string;
  symbol: string;
  shortLegs: OptionLegQuery[];
}

/**
 * Mirrors `MAX_GROUPS` in the api's `services/market/legs-batch.ts`. The endpoint rejects a
 * larger batch with a 400 rather than truncating it, so exceeding this fails the whole query —
 * which is why the board is chunked here instead of being allowed to grow into the cap.
 */
const MAX_LEG_GROUPS = 40;

/**
 * The whole board's Greeks — every row's short legs as one group keyed by `rowKey`.
 * `staleTime` matches the endpoint's own 60s KV TTL, so a remount inside the window costs
 * nothing. There is deliberately NO `refetchInterval`: the price line already refreshes every
 * minute through `useQuotes`, and a forty-group fan-out on a timer is not something a phone on a
 * cellular connection should be doing in the background.
 *
 * **One query, possibly several requests.** A board past the endpoint's cap is split into
 * requests of at most `MAX_LEG_GROUPS` inside the `queryFn` and merged back in the board's own
 * order — the cache key stays derived from the FULL group list, so the board is still one cache
 * entry and one loading state, and a 41st open chain does not silently strip the odds off all
 * 41 rows.
 */
export function useRiskBoardGreeks(rows: RiskBoardRow[]) {
  const groups = rows
    .filter((row) => row.shortLegs.length > 0)
    .map((row) => ({ id: row.rowKey, underlyingSymbol: row.symbol, legs: row.shortLegs }));

  return useQuery({
    queryKey: quoteKeys.legsBatch(groups),
    queryFn: async () => {
      const batches: Array<typeof groups> = [];
      for (let i = 0; i < groups.length; i += MAX_LEG_GROUPS) {
        batches.push(groups.slice(i, i + MAX_LEG_GROUPS));
      }
      // `Promise.all` resolves in argument order regardless of completion order, so flattening
      // it restores the board's order without matching anything up by id.
      const responses = await Promise.all(batches.map((batch) => quotesApi.getOptionLegsBatch(batch)));
      return { groups: responses.flatMap((response) => response.groups) };
    },
    enabled: groups.length > 0,
    staleTime: 60 * 1000,
  });
}

export function useSpreadSuggestions(
  params: {
    underlyingSymbol: string;
    existingSide: 'put' | 'call';
    spreadWidth: number;
    quantity: number;
    targetDeltaMin?: number;
    targetDeltaMax?: number;
  },
  enabled = true
) {
  return useQuery({
    queryKey: [...quoteKeys.all, 'spreadSuggestions', JSON.stringify(params)],
    queryFn: () => quotesApi.getSpreadSuggestions(params),
    enabled: enabled && !!params.underlyingSymbol,
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });
}
